import { useSnapshotJobs } from "../stage-branches/SnapshotJobs";
import ReadModelInfo from "../stage-branches/components/ReadModelInfo";
import ReadModelEvidence from "../stage-branches/components/ReadModelEvidence";
import CicdWebhookSettings from "../components/CicdWebhookSettings";
import useCicdEvents from "../hooks/useCicdEvents";
import { applyMonitorUpdate } from "../hooks/monitorUpdates";
import { ExecutionErrors, executionOf } from "../components/CicdExecution";
import Alert from "@mui/material/Alert";
/**
 * CicdPipeline - Admin-Seite /cicd-pipeline, in der Oberflaeche "CI/CD Monitor".
 *
 * Der Name lautet bewusst CI/CD Monitor und nicht Pipeline Monitor: die Webapp hat unter
 * /pipeline schon eine Pipeline-Ansicht fuer das SAP Pipeline Concept, und zwei Seiten mit
 * "Pipeline" im Namen zeigten auf voellig verschiedene Dinge. Die Route bleibt /cicd-pipeline,
 * damit bestehende Links und Lesezeichen weiter funktionieren.
 *
 * MUI repository disclosure and searchable transport history share the existing Space context.
 *
 * Status snapshots follow authenticated server events and reconnects. Alle Umlaute echt (ae/oe/ue verboten).
 * Bewegung: 150-300ms, ausschliesslich transform/opacity, per prefers-reduced-motion stillgelegt.
 * Farben: nur --iio-*-Tokens, Toenungen ueber color-mix(in srgb, var(--iio-*) X%, transparent).
 * Alle Klassen cp-* gescoped unter .cp-root.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation, Trans } from "react-i18next";
import { api, getToken, hatCicdZugang, cicdSpacesVon } from "../utils/helpers";
import { useToast, ToastContainer } from "../components/ui";
import CicdAutomations from "../components/CicdAutomations";
import { packageTransportText } from "../utils/transportPackageErrors";
import CicdRunHistory, { instanceNameForEnvironment, timeAgo } from "../components/CicdRunHistory";
import useCicdHistory from "../hooks/useCicdHistory";
import { pilotCall, useStageBranchesConfig } from "../stage-branches/useStageBoard";
import BranchRules from "../stage-branches/components/BranchRules";
import { MonitorButton as Button, MonitorSelect, MonitorDialog } from "../components/MonitorControls";
import AccountTreeRounded from "@mui/icons-material/AccountTreeRounded";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import RocketLaunchOutlined from "@mui/icons-material/RocketLaunchOutlined";
import LayersOutlined from "@mui/icons-material/LayersOutlined";
import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import "./CicdPipeline.css";
import { STAGES, stageLabel } from "../utils/stages";


// Die Stufenreihe kommt aus utils/stages (DEV, QAS, PRE-PROD, PROD). Gerendert wird nur, was
// der Space wirklich hat - eine leere QAS-Kachel in einer bewusst kurzen Schiene behauptete
// einen Mangel, den es nicht gibt (Davids Entscheidung 31.07.2026).
const CANON = STAGES.map(s => ({ stage: s.key, env: s.env }));
const HIDDEN_OPERATION_STATUSES = new Set(["discarded", "rejected", "withdrawn", "invalidated"]);

export default function CicdPipeline({ user, spaces = [], onCicdRefresh }) {
  const { t, i18n } = useTranslation(["cicdmonitor", "gemeinsam"]);
  const { toasts, addToast } = useToast();
  const isAdmin = hatCicdZugang(user, spaces);
  const waehlbareSpaces = useMemo(() => cicdSpacesVon(user, spaces), [user, spaces]);
  const [linkTarget] = useState(() => Object.fromEntries(new URLSearchParams(window.location.search)));
  const [spaceId, setSpaceId] = useState(() => cicdSpacesVon(user, spaces).find(space => space.id === linkTarget.spaceId)?.id || (linkTarget.spaceId ? "" : cicdSpacesVon(user, spaces)[0]?.id || ""));
  const focusedLink = useRef(null);
  const [linkedRun, setLinkedRun] = useState(null);
  const [linkedOperation, setLinkedOperation] = useState(null);
  const [linkedError, setLinkedError] = useState(null);
  const [eventRevision, setEventRevision] = useState(0);
  const [loadedData, setData] = useState(null);
  const activeSpaceRef = useRef(spaceId);
  activeSpaceRef.current = spaceId;
  const requestRef = useRef(0);
  const overviewReading = useRef(null);
  const monitorState = useRef(null);
  const monitorFlight = useRef(null);
  const monitorTrailing = useRef(null);
  const monitorRefresh = useRef(null);
  const [loading, setLoading] = useState(true);
  const [readError, setReadError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const expandedRunRef = useRef(expandedId); expandedRunRef.current = expandedId;
  const [detail, setDetail] = useState({}); // runId -> {loading, run}
  // Repository settings load once per Space; status events only refresh transport snapshots.
  const [loadedAutos, setAutos] = useState(null);
  const autos = loadedAutos?.spaceId === spaceId ? loadedAutos : { loading: true }; // { loading, data, error }
  const [confirmUnbind, setConfirmUnbind] = useState(false); // Verbindung-loesen-Dialog
  const [unbinding, setUnbinding] = useState(false);
  const [rerunId, setRerunId] = useState(null); // Lauf, der gerade neu gestartet wird
  // Neustart nur bei bestätigt ausgeschaltetem Schalter (Vertrag 24.1); mit Schalter wiederholt die Transportfreigabe.
  // Gelesen wird der Schalter erst, wenn ein Lauf aufgeklappt ist, denn nur dort steht der Neustart.
  const stageSwitch = useStageBranchesConfig(spaceId);
  const readModel = stageSwitch.config?.readModel;
  const localReads = readModel?.enabled === true && readModel?.localReads === true;
  let authIdentity = "";
  try { if (localReads) authIdentity = getToken(); } catch { /* An unavailable login cannot retain a projection. */ }
  const readScope = JSON.stringify([spaceId, localReads, localReads ? readModel.bindingKey : null, localReads ? readModel.generation : null, authIdentity]);
  const [repositoryScope, setRepositoryScope] = useState(null);
  const [repositoryOpenRequest, setRepositoryOpenRequest] = useState(0);
  const repositoryEnabled = !stageSwitch.loading && (!localReads || repositoryScope === readScope);
  const currentReadScope = useRef(readScope); currentReadScope.current = readScope;
  const detailAccess = useRef({ scope: readScope, denied: false });
  if (detailAccess.current.scope !== readScope) detailAccess.current = { scope: readScope, denied: false };
  const detailReads = useRef(new Map());
  const data = loadedData?.requestedSpaceId === spaceId && loadedData?.readScope === readScope ? loadedData : null;
  // DEV183: Legeaufträge stehen in der Laufhistorie statt in einem eigenen Bereich; gleiche Datenquelle wie bisher.
  const snapshotJobId = linkTarget.spaceId === spaceId ? linkTarget.snapshotJobId : undefined;
  const snapshotJobs = useSnapshotJobs({ spaceId, userId: user?.id, enabled: stageSwitch.enabled, selectedId: snapshotJobId,
    autoRefresh: !localReads, observedJobs: localReads ? data?.snapshotJobs : undefined,
    observedJobsUnavailable: localReads && data?.snapshotJobsUnavailable === true,
    observedJobsForbidden: localReads && data?.snapshotJobsForbidden === true, observationScope: localReads ? JSON.stringify([readModel.bindingKey, readModel.generation]) : null });
  const rerunAllowed = !stageSwitch.loading && !stageSwitch.error && !stageSwitch.enabled;
  // Statt des Neustarts: mit Schalter der Weg in die Transportfreigabe, bei unlesbarem Schalter „Erneut lesen“.
  const approvalsHref = `/approvals?spaceId=${encodeURIComponent(spaceId)}`;
  const openApprovals = event => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    window.history.pushState({}, "", event.currentTarget.getAttribute("href"));
    window.dispatchEvent(new PopStateEvent("popstate"));
  };
  const rerunNotice = stageSwitch.enabled ? t("freigabe:stageBranches.page.rerunNotice")
    : stageSwitch.error ? t("freigabe:stageBranches.page.rerunUnreadable") : undefined;
  const rerunNoticeAction = stageSwitch.enabled
    ? <Button component="a" size="small" href={approvalsHref} onClick={openApprovals}>{t("rc.freigabeKnopf")}</Button>
    : stageSwitch.error ? <Button size="small" onClick={stageSwitch.reload}>{t("freigabe:stageBranches.page.reload")}</Button> : null;


  const revokePilotAccess = useCallback(() => {
    if (currentReadScope.current !== readScope) return;
    requestRef.current += 1;
    detailAccess.current.denied = true;
    for (const entry of detailReads.current.values()) entry.controller.abort();
    detailReads.current.clear();
    setData(null); setDetail({}); setExpandedId(null); setLinkedRun(null); setLinkedOperation(null);
    setLoading(false);
    setReadError(`${t("zugriff.titel")}: ${t("cicdruns:execution.streamForbidden")}`);
  }, [readScope, t]);
  const load = useCallback(async (sid, silent) => {
    if (sid !== activeSpaceRef.current || localReads && detailAccess.current.denied) return;
    const scope = readScope;
    if (localReads && silent && overviewReading.current?.scope === scope) return;
    const observedMonitorState = monitorState.current;
    const requestId = ++requestRef.current;
    if (!sid) { setData(null); setLoading(false); return; }
    overviewReading.current = { scope, requestId };
    if (!silent) { setLoading(true); setReadError(null); }
    try {
      const path = `/api/cicd/pipeline/overview?spaceId=${encodeURIComponent(sid)}`;
      const r = await (localReads ? pilotCall(path, { timeoutMs: 10000 }) : api(path));
      if (localReads && getToken() !== authIdentity) return;
      if (requestId === requestRef.current && sid === activeSpaceRef.current && scope === currentReadScope.current) {
        if (localReads && (!r?.readModel || r.readModel.bindingKey !== readModel.bindingKey || r.readModel.generation !== readModel.generation))
          throw new Error(t("readModel.bindingChanged"));
        if (localReads && observedMonitorState !== monitorState.current && monitorState.current?.scope === scope) {
          setLoading(false); return;
        }
        const next = { ...r, requestedSpaceId: sid, readScope: scope };
        monitorState.current = { scope, cursor: null, data: next };
        setData(next); setReadError(null);
      }
    } catch (e) {
      if (requestId !== requestRef.current || sid !== activeSpaceRef.current || scope !== currentReadScope.current) return;
      if (localReads && [401, 403].includes(e.status)) { revokePilotAccess(); return; }
      setReadError(e.message || t("meldung.statusNichtAbrufbar"));
      if (!silent) addToast(e.message || t("meldung.statusNichtAbrufbar"), "error");
    } finally { if (overviewReading.current?.requestId === requestId) overviewReading.current = null; }
    if (requestId === requestRef.current && sid === activeSpaceRef.current && scope === currentReadScope.current) setLoading(false);
  }, [addToast, t, readScope, localReads, readModel?.bindingKey, readModel?.generation, authIdentity, revokePilotAccess]);

  // Fehlgeschlagenen/abgebrochenen Lauf ueber die Webapp neu starten (GitHub startet denselben
  // Run mit denselben Inputs neu). Danach den Status frisch ziehen - GitHub braucht einen Moment.
  const rerunRun = useCallback(async (r) => {
    setRerunId(r.id);
    try {
      const erg = await api("/api/cicd/transport/rerun", { method: "POST", timeout: 120000, body: JSON.stringify({ spaceId, runId: r.id, language: localStorage.getItem("iio-lang") === "en" ? "en" : "de" }) });
      // Neustart = neuer Lauf mit der aktuellen Erweiterung; nur ein Altlauf ohne gespeicherte
      // Eingaben faellt auf den GitHub-Rerun zurueck - das sagt der Hinweis dann ausdruecklich.
      if (erg?.modus === "neu") addToast(t("meldung.neustartNeu", { v: erg.erweiterung, id: erg.run?.id || "?" }), "success");
      else addToast(t("meldung.neustart"), "success");
      if (erg?.hinweis) addToast(erg.hinweis, "warning");
      setTimeout(() => load(spaceId, true), 2500);
    } catch (e) {
      addToast(e.message || t("meldung.neustartFehler"), "error");
    }
    setRerunId(null);
  }, [spaceId, addToast, load, t]);

  // Beim Direktaufruf der Seite sind die Spaces noch nicht geladen, spaces[0] im useState war
  // also leer und die Seite blieb auf "Kein Space gewaehlt" stehen, bis man selbst auswaehlt.
  useEffect(() => {
    if (!spaceId && waehlbareSpaces.length > 0) setSpaceId(waehlbareSpaces.find(space => space.id === linkTarget.spaceId)?.id || (linkTarget.spaceId ? "" : waehlbareSpaces[0].id));
  }, [waehlbareSpaces, spaceId, linkTarget.spaceId]);

  useEffect(() => { if (isAdmin) load(spaceId); }, [isAdmin, spaceId, load]);

  // Automatisierungen nur einmal je Space-Wechsel. Nicht in den Poll ziehen (PAT-Budget!).
  useEffect(() => {
    if (!isAdmin || !spaceId || !repositoryEnabled) { setAutos(null); return; }
    let abort = false;
    setAutos({ loading: true, spaceId });
    api(`/api/cicd/repo-actions?spaceId=${encodeURIComponent(spaceId)}`)
      .then(r => { if (!abort) setAutos({ loading: false, data: r, spaceId }); })
      .catch(e => { if (!abort) setAutos({ loading: false, spaceId, error: e.message || t("meldung.nichtAbrufbar") }); });
    return () => { abort = true; };
    // `t` bewusst NICHT in der Abhaengigkeitsliste: dieser Abruf laeuft genau einmal je
    // Space-Wechsel, ein Sprachwechsel darf ihn nicht erneut ausloesen (PAT-Budget).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, spaceId, repositoryEnabled]);

  const readMonitorUpdates = useCallback(() => {
    const scope = readScope;
    if (!localReads || !spaceId || detailAccess.current.denied) return Promise.resolve();
    if (monitorFlight.current?.scope === scope) { monitorFlight.current.pending = true; return monitorFlight.current.promise; }
    const flight = { scope, pending: false };
    const current = () => currentReadScope.current === scope && !detailAccess.current.denied && getToken() === authIdentity;
    let passes = 0;
    flight.promise = (async () => {
      do {
        passes++;
        flight.pending = false;
        if (!current()) return;
        const base = monitorState.current?.scope === scope ? monitorState.current : null;
        const update = await pilotCall(`/api/cicd/pipeline/monitor-updates?spaceId=${encodeURIComponent(spaceId)}${base?.cursor ? `&cursor=${encodeURIComponent(base.cursor)}` : ""}`, { timeoutMs: 10000 });
        if (!current()) return;
        // A manual refresh can replace the base while this request is pending.
        if (base !== monitorState.current) { flight.pending = true; continue; }
        if (update.unchanged) { setReadError(null); continue; }
        if (!update.readModel || update.readModel.bindingKey !== readModel.bindingKey || update.readModel.generation !== readModel.generation)
          throw new Error(t("readModel.bindingChanged"));
        const next = applyMonitorUpdate(base?.data, base?.cursor, update);
        if (!next) { monitorState.current = { scope, cursor: null, data: base?.data }; flight.pending = true; continue; }
        const data = { ...next, requestedSpaceId: spaceId, readScope: scope };
        monitorState.current = { scope, cursor: update.cursor, data }; setData(data); setReadError(null);
        setLinkedRun(previous => {
          if (!previous || previous.spaceId !== spaceId) return previous;
          if (update.removedRunIds?.includes(String(previous.id))) return null;
          const changed = update.runs?.find(run => String(run.id) === String(previous.id));
          return changed ? { ...changed, spaceId } : previous;
        });
        setLinkedOperation(previous => {
          if (!previous || previous.spaceId !== spaceId) return previous;
          if (update.removedOperationIds?.includes(String(previous.id))) return null;
          const changed = update.operations?.find(operation => String(operation.id) === String(previous.id));
          return changed ? { ...changed, spaceId } : previous;
        });
      } while (flight.pending && current() && passes < 2);
    })().catch(error => {
      if (!current()) return;
      if ([401, 403].includes(error.status)) revokePilotAccess();
      else setReadError(error.message || t("meldung.statusNichtAbrufbar"));
    }).finally(() => {
      if (monitorFlight.current === flight) monitorFlight.current = null;
      if (flight.pending && current()) {
        clearTimeout(monitorTrailing.current);
        monitorTrailing.current = setTimeout(() => { if (current()) monitorRefresh.current?.(); }, 1000);
      }
    });
    monitorFlight.current = flight;
    return flight.promise;
  }, [readScope, localReads, spaceId, authIdentity, readModel?.bindingKey, readModel?.generation, revokePilotAccess, t]);
  monitorRefresh.current = readMonitorUpdates;
  useEffect(() => () => clearTimeout(monitorTrailing.current), [readScope]);
  const hasActive = !!data?.runs?.some(r => ["in_progress", "queued"].includes(r.status) && !r.timedOut);
  const connection = useCicdEvents(spaceId, async () => {
    if (localReads) { await readMonitorUpdates(); return; }
    setEventRevision(value => value + 1);
    await load(spaceId, true);
  }, isAdmin, { localRevision: localReads, reconcileOnConnect: localReads });
  useEffect(() => {
    if (!isAdmin || !localReads) return;
    // Also retries a failed consumer read when the SSE cursor itself did not change.
    const timer = setInterval(() => { if (!document.hidden) readMonitorUpdates(); }, 60000);
    return () => clearInterval(timer);
  }, [isAdmin, localReads, readMonitorUpdates]);
  useEffect(() => {
    if (localReads && connection === "forbidden") revokePilotAccess();
  }, [localReads, connection, revokePilotAccess]);

  useEffect(() => () => {
    for (const entry of detailReads.current.values()) entry.controller.abort();
    detailReads.current.clear();
  }, [readScope]);
  const loadPilotRun = useCallback((runId) => {
    const id = String(runId), scope = readScope;
    if (detailAccess.current.denied) return Promise.resolve(null);
    const key = JSON.stringify([scope, id]);
    if (detailReads.current.has(key)) return detailReads.current.get(key).promise;
    const controller = new AbortController();
    const current = () => currentReadScope.current === scope && !detailAccess.current.denied && !controller.signal.aborted && getToken() === authIdentity;
    setDetail(previous => ({ ...previous, [id]: { ...previous[id], loading: true, error: null } }));
    const promise = pilotCall(`/api/cicd/transport/run/${encodeURIComponent(id)}?spaceId=${encodeURIComponent(spaceId)}`,
      // A cold GitHub job detail can exceed 30 seconds; one bounded read, no automatic retry.
      { timeoutMs: 90000, signal: controller.signal }).then(run => {
        if (!current()) return null;
        setDetail(previous => ({ ...previous, [id]: { loading: false, run } }));
        return run;
      }).catch(error => {
        if (current()) {
          if ([401, 403].includes(error.status)) revokePilotAccess();
          else {
            const code = error.code === "CICD_RUN_DETAIL_TIMEOUT" ? "runDetails.timeout"
              : error.code === "CICD_RUN_DETAIL_CAPACITY" ? "runDetails.capacity" : null;
            setDetail(previous => ({ ...previous, [id]: { ...previous[id], loading: false,
              error: code ? t(code) : error.message || t("meldung.statusNichtAbrufbar") } }));
          }
        }
        return null;
      }).finally(() => {
        const entry = detailReads.current.get(key);
        if (entry?.promise === promise) { detailReads.current.delete(key); entry.onSettled?.(); }
      });
    detailReads.current.set(key, { controller, promise });
    return promise;
  }, [readScope, spaceId, authIdentity, t, revokePilotAccess]);

  const openRunObservation = useRef(null);
  const openRun = data?.runs?.find(run => String(run.id) === String(expandedId))
    || (String(linkedRun?.id) === String(expandedId) ? linkedRun : null);
  const openRunSignature = openRun ? JSON.stringify([openRun, data?.runChanges?.[String(expandedId)]]) : null;
  useEffect(() => {
    const next = { scope: readScope, id: expandedId, signature: openRunSignature, attempt: openRun?.runAttempt || 1 };
    const previous = openRunObservation.current; openRunObservation.current = next;
    if (!localReads || !expandedId || !openRunSignature || previous?.scope !== readScope
      || String(previous.id) !== String(expandedId) || previous.signature === openRunSignature) return;
    const key = JSON.stringify([readScope, String(expandedId)]);
    const pending = detailReads.current.get(key);
    if (pending && previous.attempt === next.attempt) {
      // Job events during a slow detail read collapse to one follow-up. Only an
      // attempt change aborts the old request, so step events cannot starve it.
      pending.onSettled = () => {
        if (currentReadScope.current === readScope && String(expandedRunRef.current) === String(expandedId)) loadPilotRun(expandedId);
      };
      return;
    }
    if (pending) { pending.controller.abort(); detailReads.current.delete(key); }
    // Only the expanded, changed run may read its jobs. Old attempts cannot win.
    setDetail(previous => ({ ...previous, [expandedId]: { loading: true } }));
    loadPilotRun(expandedId);
  }, [readScope, localReads, expandedId, openRunSignature, openRun?.runAttempt, loadPilotRun]);

  const toggleRun = async (r) => {
    if (String(expandedId) === String(r.id)) { setExpandedId(null); return; }
    setExpandedId(r.id);
    const sid = spaceId, requestedScope = readScope;
    if (!detail[r.id]?.run || detail[r.id]?.error) {
      if (localReads) { await loadPilotRun(r.id); return; }
      setDetail(d => ({ ...d, [r.id]: { loading: true } }));
      try {
        const run = await api(`/api/cicd/transport/run/${r.id}?spaceId=${encodeURIComponent(spaceId)}`);
        if (activeSpaceRef.current === sid && currentReadScope.current === requestedScope) setDetail(d => ({ ...d, [r.id]: { loading: false, run } }));
      } catch (e) {
        if (activeSpaceRef.current === sid && currentReadScope.current === requestedScope) setDetail(d => ({ ...d, [r.id]: { loading: false, error: e.message } }));
      }
    }
  };

  // Aktiven Lauf offen halten -> Schritt-Detail live nachladen
  useEffect(() => {
    if (!expandedId || localReads) return;
    const r = data?.runs?.find(x => String(x.id) === String(expandedId));
    if (r || linkedRun?.spaceId === spaceId) {
      api(`/api/cicd/transport/run/${expandedId}?spaceId=${encodeURIComponent(spaceId)}`)
        .then(run => { if (activeSpaceRef.current === spaceId && currentReadScope.current === readScope) setDetail(d => ({ ...d, [expandedId]: { loading: false, run } })); })
        .catch(() => {});
    }
  }, [data, expandedId, spaceId, eventRevision, linkedRun?.spaceId, readScope, localReads]);

  // Versionspflege: SAP-Basis und abat-Erweiterung im Repository. Der Stand wird einmal je
  // Space-Wechsel geladen (wie repo-actions), NICHT im 5s-Poll (PAT-Budget).
  const [loadedUpdate, setUpdData] = useState(null);
  const updData = loadedUpdate?.spaceId === spaceId ? loadedUpdate : null;
  const [updLoading, setUpdLoading] = useState(false);
  const [updError, setUpdError] = useState(null);
  const [sapUpdating, setSapUpdating] = useState(false);
  const [extUpdating, setExtUpdating] = useState(false);
  const [updMsg, setUpdMsg] = useState(null); // { text, type:"ok"|"bad" }
  // Mode switch: rollback to SAP-only
  const [confirmRueckbau, setConfirmRueckbau] = useState(false);
  const [rueckbauBusy, setRueckbauBusy] = useState(false);
  // Laeufe, die den Rueckbau blockieren. Der Dialog bleibt dann offen und zeigt sie an - sonst
  // steht der Nutzer vor einem geschlossenen Dialog und weiss nicht, worauf er warten soll.
  const [rueckbauBlocker, setRueckbauBlocker] = useState([]);

  useEffect(() => {
    if (!isAdmin || !spaceId || !repositoryEnabled) { setUpdData(null); setUpdMsg(null); return; }
    let abort = false;
    setUpdLoading(true); setUpdMsg(null); setUpdData(null); setUpdError(null);
    api(`/api/cicd/repo-update-status?spaceId=${encodeURIComponent(spaceId)}`)
      .then(r => { if (!abort) setUpdData({ ...r, spaceId }); })
      .catch(e => { if (!abort) { setUpdData(null); setUpdError(e.message); } })
      .finally(() => { if (!abort) setUpdLoading(false); });
    return () => { abort = true; };
  }, [isAdmin, spaceId, repositoryEnabled]);

  const reloadAfterUpdate = () => {
    if (activeSpaceRef.current !== spaceId) return;
    // Beide Abschnitte neu laden: Versionsstände + Automatisierungen
    setUpdLoading(true); setUpdError(null); setUpdData(null);
    api(`/api/cicd/repo-update-status?spaceId=${encodeURIComponent(spaceId)}`)
      .then(r => { if (activeSpaceRef.current === spaceId) setUpdData({ ...r, spaceId }); })
      .catch(e => { if (activeSpaceRef.current === spaceId) setUpdError(e.message); })
      .finally(() => { if (activeSpaceRef.current === spaceId) setUpdLoading(false); });
    setAutos({ loading: true, spaceId });
    api(`/api/cicd/repo-actions?spaceId=${encodeURIComponent(spaceId)}`)
      .then(x => { if (activeSpaceRef.current === spaceId) setAutos({ loading: false, data: x, spaceId }); })
      .catch(e => { if (activeSpaceRef.current === spaceId) setAutos({ loading: false, spaceId, error: e.message || t("meldung.nichtAbrufbar") }); });
    // Health-Karten mitziehen
    load(spaceId, true);
  };

  // Check the selected Space connection. Personal credentials are managed in setup.
  const [patCheck, setPatCheck] = useState(null);
  const [patBusy, setPatBusy] = useState(false);
  const [patMsg, setPatMsg] = useState(null);
  useEffect(() => {
    setPatCheck(null); setPatMsg(null); setPatBusy(false);
    if (!isAdmin || !spaceId || !repositoryEnabled) return;
    let abort = false;
    api(`/api/cicd/github-token-check?spaceId=${encodeURIComponent(spaceId)}`)
      .then(r => { if (!abort) setPatCheck({ ...r, requestedSpaceId: spaceId }); })
      .catch(() => { if (!abort) setPatCheck(null); });
    return () => { abort = true; };
  }, [isAdmin, spaceId, repositoryEnabled]);
  const patProblem = !!(patCheck?.requestedSpaceId === spaceId && patCheck?.tokenSet && patCheck.ok === false);
  const recheckPat = async () => {
    const sid = spaceId;
    setPatBusy(true); setPatMsg(null);
    try {
      const r = await api(`/api/cicd/github-token-check?spaceId=${encodeURIComponent(sid)}`);
      if (activeSpaceRef.current !== sid) return;
      setPatCheck({ ...r, requestedSpaceId: sid });
      if (r?.ok) { setPatMsg(null); reloadAfterUpdate(); }
      else setPatMsg({ type: "bad", text: t("pat.weiterAbgelehnt", { status: r?.status ?? "?" }) });
    } catch (e) {
      if (activeSpaceRef.current !== sid) return;
      setPatMsg({ type: "bad", text: e.message || t("pat.fehlgeschlagen") });
    }
    setPatBusy(false);
  };

  // "Nach Updates suchen": fragt den Server neu ab - der vergleicht den Kundenstand mit dem
  // von uns freigegebenen Stand und meldet einen neueren SAP-HEAD transparent als ungeprueft.
  const checkUpdates = () => {
    setUpdLoading(true); setUpdMsg(null); setUpdData(null); setUpdError(null);
    api(`/api/cicd/repo-update-status?spaceId=${encodeURIComponent(spaceId)}`)
      .then(r => { if (activeSpaceRef.current === spaceId) { setUpdData({ ...r, spaceId }); setUpdError(null); } })
      .catch(e => { if (activeSpaceRef.current === spaceId) { setUpdData(null); setUpdError(e.message); } })
      .finally(() => { if (activeSpaceRef.current === spaceId) setUpdLoading(false); });
  };

  const updateSapBase = async () => {
    if (!data?.canManage) return;
    setSapUpdating(true); setUpdMsg(null);
    try {
      const r = await api("/api/cicd/update-sap-base", { method: "POST", body: JSON.stringify({ spaceId }) });
      if (activeSpaceRef.current !== spaceId) return;
      setUpdMsg({ text: r.geaendert ? t("version.sapAktualisiert", { sha: r.nachSha }) : t("version.warAktuell"), type: "ok" });
      reloadAfterUpdate();
    } catch (e) {
      if (activeSpaceRef.current === spaceId) setUpdMsg({ text: e.message || t("version.fehlgeschlagen"), type: "bad" });
    }
    setSapUpdating(false);
  };

  const updateExtension = async () => {
    if (!data?.canManage) return;
    setExtUpdating(true); setUpdMsg(null);
    try {
      const r = await api("/api/cicd/update-extension", { method: "POST", body: JSON.stringify({ spaceId }) });
      if (activeSpaceRef.current !== spaceId) return;
      // Pakettransport: geschriebene Dateien nennen; ohne ganzes Paket (KEIN_GANZES_PAKET) bleibt die Liste weg.
      const packageFiles = Array.isArray(r?.paketTransport?.geschriebeneDateien) ? r.paketTransport.geschriebeneDateien.filter(f => f?.repo && f?.pfad) : [];
      // Abbruch ohne Schreiben: abweichende SAP-Dateien (Server nennt sie) oder ein Verweis, der sich nicht ohne Token umschreiben lässt.
      const packageCode = r?.paketTransport?.code;
      const packageError = packageCode === "PACKAGE_TRANSPORT_BASE_DIVERGED" ? (r.paketTransport.fehler || null)
        : packageCode === "PACKAGE_TRANSPORT_PATCH_UNSAFE" ? (packageTransportText(packageCode) || r.paketTransport.fehler || null) : null;
      setUpdMsg(packageError ? { text: packageError, type: "bad" }
        : { text: r.geaendert ? t("version.erwGehoben", { version: r.nachVersion }) : t("version.warAktuell"), type: "ok", packageFiles });
      reloadAfterUpdate();
    } catch (e) {
      if (activeSpaceRef.current === spaceId) setUpdMsg({ text: e.message || t("version.fehlgeschlagen"), type: "bad" });
    }
    setExtUpdating(false);
  };

  // Rollback: remove extension files, switch to SAP-only mode.
  const doRueckbau = async () => {
    if (!data?.canManage) return;
    setRueckbauBusy(true);
    try {
      const r = await api("/api/cicd/remove-extension", { method: "POST", body: JSON.stringify({ spaceId }) });
      if (r.ok) {
        addToast(t("modus.rueckbauErfolg"), "success");
        setConfirmRueckbau(false);
        reloadAfterUpdate();
      } else {
        addToast(r.error || t("modus.rueckbauFehler"), "error");
      }
    } catch (e) {
      // 409 mit dieser Kennung: Es laeuft noch etwas. Dialog offen lassen und die Laeufe nennen.
      if (e.code === "TRANSPORT_LAEUFT") {
        setRueckbauBlocker(Array.isArray(e.body?.laufend) ? e.body.laufend : []);
        addToast(t("modus.rueckbauGesperrt"), "error");
      } else {
        addToast(e.message || t("modus.rueckbauFehler"), "error");
        setConfirmRueckbau(false);
      }
    }
    setRueckbauBusy(false);
  };

  const doUnbind = async () => {
    if (!data?.canManage) return;
    setUnbinding(true);
    try {
      const r = await api("/api/cicd/unbind", { method: "POST", body: JSON.stringify({ spaceId }) });
      addToast(t("meldung.verbindungGeloest", { repo: r.repo }), "success");
      setConfirmUnbind(false);
      load(spaceId);
      // Ladezustand, NICHT null: bis load() antwortet (ein Roundtrip) rendert der Abschnitt
      // sonst seinen Leer-Zweig und behauptet "Keine Workflows gefunden - das Content-Repository
      // liefert bisher keine Automatisierungen aus". Das waere die falsche Begruendung: es gibt
      // gerade keine Bindung mehr, das Repository ist unveraendert. Danach verschwindet der
      // Abschnitt ohnehin, weil configured auf false faellt.
      setAutos({ loading: true, spaceId });
      // Die Markierung an den Spaces (Sidebar, Karten, Space-Kopf) haengt an einem Aufruf, der
      // sonst nur beim Start laeuft. Ohne dieses Nachladen zeigte sie den geloesten Space
      // weiterhin als eingerichtet an.
      onCicdRefresh?.();
    } catch (e) {
      addToast(e.message || t("meldung.verbindungFehler"), "error");
    } finally {
      setUnbinding(false);
    }
  };

  const liveRuns = useMemo(() => {
    const runs = data?.runs || [];
    return linkedRun?.spaceId === spaceId && !runs.some(run => String(run.id) === String(linkedRun.run.id)) ? [linkedRun.run, ...runs] : runs;
  }, [data?.runs, linkedRun, spaceId]);
  const history = useCicdHistory(spaceId, isAdmin && data?.configured === true && !(localReads && ["pending", "unavailable"].includes(data?.readModel?.state)), liveRuns, eventRevision);

  useEffect(() => {
    setExpandedId(null); setDetail({}); setLinkedRun(null); setLinkedOperation(null); focusedLink.current = null;
    setConfirmUnbind(false); setConfirmRueckbau(false); setRueckbauBlocker([]);
    setSapUpdating(false); setExtUpdating(false); setUpdMsg(null);
  }, [spaceId, readScope]);
  useEffect(() => {
    if (!isAdmin || !spaceId || spaceId !== linkTarget.spaceId) return;
    let cancelled = false;
    setLinkedError(null);
    if (linkTarget.runId) {
      const runId = String(linkTarget.runId);
      const focusKey = `${spaceId}:${runId}`;
      const shouldFocus = focusedLink.current !== focusKey;
      if (shouldFocus) { setExpandedId(runId); focusedLink.current = focusKey; }
      if (!localReads) setDetail(previous => ({ ...previous, [runId]: { loading: true } }));
      (localReads ? loadPilotRun(runId) : api(`/api/cicd/transport/run/${encodeURIComponent(runId)}?spaceId=${encodeURIComponent(spaceId)}`))
        .then(run => {
          if (cancelled || !run) return;
          setLinkedRun({ spaceId, run: { ...run, id: runId } });
          setDetail(previous => ({ ...previous, [runId]: { loading: false, run } }));
          if (shouldFocus) requestAnimationFrame(() => requestAnimationFrame(() =>
            [...document.querySelectorAll("[data-run-id]")].find(node => node.getAttribute("data-run-id") === runId)?.scrollIntoView?.({ block: "start" })));
        }).catch(error => { if (!cancelled) setLinkedError(error.message); });
    }
    if (linkTarget.operationId) {
      const opId = String(linkTarget.operationId);
      api(`/api/cicd/pipeline/operations/${encodeURIComponent(opId)}?spaceId=${encodeURIComponent(spaceId)}`)
        .then(result => {
          if (cancelled || localReads && (detailAccess.current.denied || currentReadScope.current !== readScope || getToken() !== authIdentity)) return;
          setLinkedOperation({ spaceId, operation: result.operation || result });
          requestAnimationFrame(() => requestAnimationFrame(() =>
            [...document.querySelectorAll("[data-operation-id]")].find(node => node.getAttribute("data-operation-id") === opId)?.scrollIntoView?.({ block: "start" })));
        })
        .catch(error => { if (!cancelled) setLinkedError(error.message); });
    }
    return () => { cancelled = true; };
  }, [isAdmin, spaceId, linkTarget.spaceId, linkTarget.runId, linkTarget.operationId, eventRevision, readScope, localReads, loadPilotRun, authIdentity]);



    // Sperre auf den CI/CD-Zugang (global ODER Space-Admin) - nicht mehr auf die globale Rolle.
  if (user && !isAdmin) {
    return (
      <div className="cp-root">
        <div className="cp-denied"><h2>{t("zugriff.titel")}</h2><p>{t("zugriff.text")}</p></div>
        <ToastContainer toasts={toasts} />
      </div>
    );
  }

  const health = data?.health || {};
  const project = data?.project;
  const stages = data?.stages || [];
  const active = data?.active || [];
  const activeEnvs = new Set(active.map(a => a.targetEnv).filter(Boolean));
  // Eingerichtet, aber GitHub getrennt: der Verlauf steht noch da, handeln kann die Pipeline
  // nicht. Dieselbe Unterscheidung benutzt die Transport-Freigabe (tokenSet, nicht configured).
  const getrennt = data?.configured === true && health.tokenSet === false;
  const runsUnknown = !!data?.runsUnknown;
  const gotoSetup = (e) => { e.preventDefault(); window.history.pushState({}, "", "/cicd-setup"); window.dispatchEvent(new PopStateEvent("popstate")); };

  const configured = data?.configured === true;
  const repoLabel = project ? `${project.repoOwner}/${project.contentRepo}` : "";

  // Stage-Fluss aus kanonischer DEV/QAS/PROD-Reihe, angereichert mit vorhandenen Instanzen
  // Nur Stufen, die der Space wirklich hat. Ist gar keine getaggt, zeigt die Reihe die
  // vollstaendige Vorlage - dann ist "fehlt" die richtige Aussage.
  const vorhandeneStufen = new Set(stages.map(x => x.stage));
  const reihe = vorhandeneStufen.size > 0 ? CANON.filter(c => vorhandeneStufen.has(c.stage)) : CANON;
  const flowNodes = reihe.map(c => {
    const s = stages.find(x => x.stage === c.stage);
    const present = !!s;
    const credsReady = !!s?.credsReady;
    const synced = !!s?.synced; // GitHub-Environment existiert wirklich
    const state = !present ? "missing" : (synced && credsReady ? "ready" : (credsReady ? "pending" : "incomplete"));
    return { stage: c.stage, env: s?.targetEnv || c.env, instanceName: s?.instanceName || null, present, synced, credsReady, state, active: activeEnvs.has(s?.targetEnv || c.env) };
  });

  const hc = (ok, bad) => ok ? "ok" : (bad ? "bad" : "");

  return (
    <div className="cp-root">
      <header className="cp-hero">
      <div className="cp-ph">
        <div className="crumb">{t("kopf.brotkrumeAdmin")} <b>&gt;&gt;</b> {t("kopf.brotkrumeCicd")} <b>&gt;&gt;</b> {t("kopf.brotkrumeAktuell")}</div>
        <div className="cp-title"><h1>{t("kopf.titel")}</h1></div>
        <div className="sub">{t("kopf.sub")}</div>
      </div>

      <div className="cp-bar">
        <MonitorSelect aria-label={t("leiste.space")} className="cp-space-select" value={spaceId}
          disabled={sapUpdating || extUpdating || rueckbauBusy || unbinding || !!rerunId}
          onChange={e => { setSpaceId(e.target.value); const url = new URL(window.location.href); url.search = new URLSearchParams({ spaceId: e.target.value }).toString(); window.history.replaceState(null, "", url); }}>
          <option value="">{t("leiste.spaceWaehlen")}</option>
          {waehlbareSpaces.map(sp => <option key={sp.id} value={sp.id}>{sp.name}</option>)}
        </MonitorSelect>
        <Button type="button" className="cp-refresh" onClick={() => { load(spaceId); history.refresh(); if (localReads) { setEventRevision(value => value + 1); snapshotJobs.refresh(); if (expandedId) loadPilotRun(expandedId); } }} disabled={!spaceId || loading}>
          <svg viewBox="0 0 24 24"><path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
          {t("gemeinsam:aktion.aktualisieren")}
        </Button>
        <span className={"cp-live" + (!localReads && hasActive ? " on" : "")}>
          {!localReads && <span className="beat" />}
          {localReads && connection !== "forbidden" ? <ReadModelInfo key={readScope} label={t("leiste.liveUpdates")} message={t(`readModel.${!data ? "pending" : ["pending", "unavailable", "stale"].includes(data.readModel?.state) ? data.readModel.state : "ready"}`)} metadata={data?.readModel} fallbackReason={t("readModel.refreshFailed")}/> : connection === "connected" ? t("cicdruns:execution.streamConnected")
            : connection === "forbidden" ? t("cicdruns:execution.streamForbidden")
            : connection === "reconnecting" ? t("cicdruns:execution.streamReconnecting")
            : t("cicdruns:execution.streamConnecting")}
        </span>
      </div>

      {configured && (
          <div className="cp-health">
            <div className={"cp-hcard " + hc(health.tokenSet, !health.tokenSet)}>
              <div className="ic"><svg viewBox="0 0 24 24"><path d="M9 19c-4 1.5-4-2.5-6-3m12 5v-3.5c0-1 .1-1.4-.5-2 2.8-.3 5.5-1.4 5.5-6a4.6 4.6 0 0 0-1.3-3.2 4.3 4.3 0 0 0-.1-3.2s-1-.3-3.4 1.3a11.7 11.7 0 0 0-6.2 0C6.6 2.8 5.6 3.1 5.6 3.1a4.3 4.3 0 0 0-.1 3.2 4.6 4.6 0 0 0-1.3 3.2c0 4.6 2.7 5.7 5.5 6-.6.6-.6 1.2-.5 2V21"/></svg></div>
              <div className="tx"><div className="k">{t("health.github")}</div><div className="v">{health.tokenSet ? (health.login || t("health.githubVerbunden")) : t("health.githubNichtVerbunden")}</div></div>
            </div>
            <div className={"cp-hcard " + hc(health.repoPrivate === true, health.repoPrivate === false)}>
              <div className="ic"><svg viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></div>
              <div className="tx"><div className="k">{t("health.repository")}</div><div className="v">{health.repoPrivate === true ? t("health.repoPrivat") : health.repoPrivate === false ? t("health.repoOeffentlich") : t("health.unbekannt")}</div></div>
            </div>
            <div className={"cp-hcard " + hc(health.workflowActive === true, health.workflowActive === false)}>
              <div className="ic"><svg viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m22 4-10 10.01-3-3"/></svg></div>
              <div className="tx"><div className="k">{t("health.workflow")}</div><div className="v">{health.workflowActive === true ? t("health.workflowAktiv") : health.workflowActive === false ? t("health.workflowInaktiv") : t("health.unbekannt")}</div></div>
            </div>
            <div className={"cp-hcard " + hc(health.stagesReady, !health.stagesReady)}>
              <div className="ic"><svg viewBox="0 0 24 24"><path d="M20 6 9 17l-5-5"/></svg></div>
              <div className="tx"><div className="k">{t("health.stages")}</div><div className="v">{health.stagesReady ? t("health.stagesBereit") : t("health.stagesUnvollstaendig")}</div></div>
            </div>
          </div>)}
      </header>

      {configured && <nav className="cp-nav" aria-label={t("navigation.label")}>
        {[["runs", HistoryRounded], ["landscape", AccountTreeRounded], ["repository", Inventory2Outlined], ["release", RocketLaunchOutlined]].map(([id, Icon]) => <Button key={id} component="a" href={`#cp-${id}`} onClick={event => {
          event.preventDefault();
          if (localReads && id === "repository") { setRepositoryScope(readScope); setRepositoryOpenRequest(value => value + 1); }
          const section = document.getElementById(`cp-${id}`);
          section?.focus({ preventScroll: true });
          section?.scrollIntoView({ block: "start", behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth" });
        }}><Icon aria-hidden="true"/>{t(`navigation.${id}`)}</Button>)}
      </nav>}

      {readError && data && <Alert severity="error" variant="outlined" sx={{ my: 1.5, borderRadius: "6px", color: "var(--iio-text-1)", borderColor: "var(--iio-critical)",
        backgroundColor: "transparent", overflowWrap: "anywhere", "& .MuiAlert-icon": { color: "var(--iio-critical)" } }}>{readError}</Alert>}

      {!spaceId ? (
        <div className="cp-empty"><b>{t("leer.keinSpaceTitel")}</b>{t("leer.keinSpaceText")}</div>
      ) : (loading || localReads && !readError) && !data ? (
        <div className="cp-empty"><span className="cp-spin" /> {t("leer.laedt")}</div>
      ) : readError && !data ? (
        <div className="cp-empty" role="alert">{readError}</div>
      ) : localReads && ["pending", "unavailable"].includes(data?.readModel?.state) ? (
        <Alert severity={data.readModel.state === "unavailable" ? "error" : "info"} variant="outlined" role="status">
          {t(`readModel.${data.readModel.state}`)}
          <ReadModelEvidence metadata={data.readModel} fallbackReason={t("readModel.refreshFailed")}/>

        </Alert>
      ) : data && data.configured === false ? (
        /* Landet man hier NACH dem Loesen der Bindung, sagte der Unbind-Dialog gerade "Ueber
           CI/CD Setup laesst sich die Verbindung jederzeit neu herstellen" - dann muss der
           Verweis auch klickbar sein, sonst faellt der versprochene Weg aus. Derselbe Link-Stil
           wie im getrennt-Zustand des Release-Kandidaten (a onClick={gotoSetup}). */
        <div className="cp-empty">
          <b>{t("leer.nichtEingerichtetTitel")}</b>
          {data?.canManage ? <Trans t={t} i18nKey="leer.nichtEingerichtetText" components={{
            setup: data?.canManage ? <a href="/cicd-setup" onClick={gotoSetup} style={{ color: "var(--iio-info)", fontWeight: 600, textDecoration: "none" }}>CI/CD Setup</a> : <span />,
          }} /> : t("leer.adminEinrichtung")}
        </div>
      ) : (
        <>
          {/* Abgelaufener/widerrufener PAT: Ursache ganz oben benennen, bevor die
              Folgefehler (401 an allen Ecken) den Blick verstellen. role=alert, damit
              Screenreader die Ursache vor den Karten ansagen. */}
          {patProblem && (
            <div className="cp-pat-banner" role="alert">
              <div className="ttl">{t("pat.titel")}</div>
              <div className="txt">{t(data?.canManage ? "pat.text" : "pat.memberText")}</div>
              <div className="row">
                {data?.canManage && <Button component="a" href="/cicd-setup" className="iio-btn iio-btn--signal" onClick={gotoSetup}>{t("pat.setup")}</Button>}
                <Button type="button" className="iio-btn iio-btn--secondary" disabled={patBusy} onClick={recheckPat}>
                  {t("pat.verlaengert")}
                </Button>
              </div>
              {patMsg && <div className={"msg " + patMsg.type}>{patMsg.text}</div>}
            </div>
          )}


          <section id="cp-runs" tabIndex={-1} aria-label={t("navigation.runs")} className="cp-section-anchor">
          <div className={"cp-activity" + (active.length ? " is-active" : "")}>
            <div className="cp-sh"><span className="cp-section-icon"><RocketLaunchOutlined aria-hidden="true"/></span><h2>{t("aktiv.titel")}</h2><span className="cnt">{active.length ? t("aktiv.anzahl", { n: active.length }) : ""}</span></div>
            {active.length === 0 ? (
              runsUnknown ? (
                <div className="cp-idle">
                  <div className="big">{t("aktiv.nichtAbrufbarTitel")}</div>
                  {t("aktiv.nichtAbrufbarText")}
                </div>
              ) : (
                <div className="cp-idle"><div className="big">{t("aktiv.keinTransportTitel")}</div>{t(localReads ? "aktiv.keinTransportTextManual" : "aktiv.keinTransportText")}</div>
              )
            ) : (
              <div className="cp-active">
                {active.map(a => (
                  <div className="cp-arun" key={a.id}>
                    <div className="top">
                      <span className="spin" />
                      <span className="to">{t("aktiv.transportNach")} <span className="env" title={a.targetEnv || undefined}>{instanceNameForEnvironment(a.targetEnv, stages)}</span></span>
                      {a.packageId && <span className="pkg">{a.packageId}</span>}
                    </div>
                    <div className="now">
                      {a.currentStep
                        ? <>{t("aktiv.aktuellerSchritt")} <b>{a.currentStep}</b>{a.currentJob ? ` (${a.currentJob})` : ""}</>
                        : a.currentJob ? <>{t("aktiv.aktuellerJob")} <b>{a.currentJob}</b></> : t("aktiv.wirdVorbereitet")}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {["reconnecting", "forbidden"].includes(connection) && <Alert severity="warning">{t(`cicdruns:execution.${connection === "forbidden" ? "streamForbidden" : "streamReconnecting"}`)}</Alert>}
          {linkedError && <Alert severity="error">{t("cicdruns:execution.loadFailed")} {linkedError}</Alert>}
          {(() => {
            const allOps = [...new Map([...(linkedOperation?.spaceId === spaceId ? [linkedOperation.operation] : []), ...(data?.operations || [])].map(op => [op.id || op.operationId, op])).values()]
              .filter(op => !HIDDEN_OPERATION_STATUSES.has(op.status))
              .sort((a, b) => (b.requestedAt || "").localeCompare(a.requestedAt || ""));
            if (!allOps.length) return null;
            const locale = i18n.language?.startsWith("en") ? "en-GB" : "de-DE";
            const linkedOpId = linkTarget.operationId;
            return <section className="iio crh-root" aria-label={t("cicdruns:execution.operations")} style={{ marginBottom: 24 }}>
              <h3 className="crh-operations-heading">
                <AssignmentOutlinedIcon aria-hidden="true" />
                {t("cicdruns:execution.operations")}
                <span className="crh-section-count">{t("cicdruns:execution.operationsCount", { count: allOps.length })}</span>
              </h3>
              <div className="crh-list">
              {allOps.map(operation => {
                const opId = operation.id || operation.operationId;
                const execution = executionOf(operation);
                const statusKey = execution?.status || operation.status || "unknown";
                const cls = { completed: "ok", failed: "bad", partial: "bad", running: "run", queued: "wait", pending: "wait", unknown: "grey", not_started: "grey" }[statusKey] || "grey";
                const statusLabel = t(`cicdruns:execution.status.${["pending", "queued", "running", "completed", "partial", "failed", "unknown", "not_started"].includes(statusKey) ? statusKey : "unknown"}`);
                const StatusIcon = { ok: CheckCircleOutlineRounded, bad: ErrorOutlineRoundedIcon, run: PlayCircleOutlineRoundedIcon, wait: HourglassTopRoundedIcon }[cls] || HelpOutlineRoundedIcon;
                const title = operation.topicName
                  || operation.items?.map(item => item.packageName || item.packageId).filter(Boolean).join(", ")
                  || operation.packageName || operation.packageId
                  || (operation.requestedAt ? t("cicdruns:execution.requestFrom", { date: new Date(operation.requestedAt).toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "numeric" }) }) : opId);
                const artifacts = (operation.items || []).flatMap(item => item.artifactIds || []).filter(Boolean);
                const packageNames = (operation.items || []).map(item => item.packageName || item.packageId).filter(Boolean);
                const preview = artifacts.length > 0
                  ? artifacts.slice(0, 3).join(", ") + (artifacts.length > 3 ? ` · ${t("cicdruns:lauf.weitereArtefakte", { count: artifacts.length - 3 })}` : "")
                  : packageNames.length > 0 ? t("cicdruns:execution.operationPackages", { count: packageNames.length }) : "";
                // Mehrere Ziele je Stufe (Monitor Schema 2): die Läufe stehen je Ziel unter items[].targets.
                const hasRun = execution?.runId || operation.items?.some(item => [item, ...Object.values(item.targets || {})]
                  .some(entry => /^\d+$/.test(String(entry?.execution?.runId || entry?.runId || "")))) || operation.laeufe?.some(item => item.run?.id) || execution?.errorDetails?.some(error => error.runId);
                const isPending = operation.status === "pending";
                const start = operation.requestedAt;
                const startTs = start ? new Date(start).getTime() : NaN;
                const absoluteTime = Number.isFinite(startTs) ? new Date(start).toLocaleString(locale) : "";
                const date = absoluteTime ? new Date(start).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" }) : "";
                const clock = absoluteTime ? new Date(start).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }) : "";
                const age = timeAgo(start, t);
                const isLinked = String(linkedOpId) === String(opId);
                return <div key={opId} data-operation-id={String(opId)} className={`crh-operation crh-operation--${cls}${isLinked ? " crh-operation--linked" : ""}`}>
                  <div className="crh-op-grid">
                    <span className="crh-status-tab">
                      <span className={`crh-status crh-status--${cls}`}><StatusIcon aria-hidden="true" />{statusLabel}</span>
                    </span>
                    <span className="crh-main">
                      <span className="crh-package-icon"><Inventory2Outlined aria-hidden="true" /></span>
                      <span className="crh-package-content">
                        <span className="crh-title">{title}</span>
                        {preview && <span className="crh-preview">{preview}</span>}
                        {(operation.transport === "package" || (operation.items || []).some(item => item?.transport === "package"))
                          && <span className="crh-history-note">{t("cicdruns:lauf.ganzesPaket")}</span>}
                      </span>
                    </span>
                    {operation.sourceInstanceName && operation.targetInstanceName && <span className="crh-route">
                      <span className="crh-stage crh-stage--source">
                        <span className="crh-stage-icon"><StorageRoundedIcon aria-hidden="true" /></span>
                        <span><span className="crh-stage-label">{t("cicdruns:lauf.quelle")}</span>{" "}<strong>{operation.sourceInstanceName}</strong></span>
                      </span>
                      <span className="crh-route-chevrons" aria-hidden="true">
                        <ChevronRightRoundedIcon /><ChevronRightRoundedIcon /><ChevronRightRoundedIcon />
                      </span>
                      <span className="crh-stage crh-stage--target">
                        <span className="crh-stage-icon"><FlagOutlinedIcon aria-hidden="true" /></span>
                        <span><span className="crh-stage-label">{t("cicdruns:lauf.ziel")}</span>{" "}<strong>{operation.targetInstanceName}</strong></span>
                      </span>
                    </span>}
                    <span className="crh-time">
                      {absoluteTime ? <><time dateTime={new Date(start).toISOString()} title={absoluteTime}>
                        <CalendarTodayOutlinedIcon aria-hidden="true" /><span>{date} · {clock}</span>
                      </time><span className="crh-age">{age}</span></> : <span>{t("cicdruns:lauf.zeitUnbekannt")}</span>}
                    </span>
                    {operation.requestedByName && <span className="crh-requester">
                      <PersonOutlineRoundedIcon aria-hidden="true" />{operation.requestedByName}
                    </span>}
                    {!hasRun && !isPending && <div className="crh-op-notice">{t("cicdruns:execution.noRunNotStarted")}</div>}
                    {!hasRun && isPending && <div className="crh-op-notice">{t("cicdruns:execution.noRunPending")}</div>}
                    <ExecutionErrors execution={execution} />
                  </div>
                </div>;
              })}
              </div>
            </section>;
          })()}
          <CicdRunHistory {...history} preserveDetailContent={localReads} stages={stages} onQueryChange={history.setQuery} onLoadMore={history.loadMore}
            runsUnknown={runsUnknown} disconnected={getrennt} expandedId={expandedId} detail={detail}
            onToggleRun={toggleRun} rerunId={rerunId} onRerun={rerunAllowed ? rerunRun : undefined}
            rerunNotice={rerunNotice} rerunNoticeAction={rerunNoticeAction}
            jobs={stageSwitch.enabled ? snapshotJobs.state.jobs : []} spaceId={spaceId} selectedJobId={snapshotJobId}
            jobSource={[stages[0]?.stage ? stageLabel(stages[0].stage) : "", stages[0]?.instanceName].filter(Boolean).join(" · ")}
            onCancelJob={snapshotJobs.cancel} cancellingJobId={snapshotJobs.cancelling}
            jobsError={stageSwitch.enabled ? snapshotJobs.state.error : null} onRefreshJobs={snapshotJobs.refresh}/>
          </section>

          <section id="cp-landscape" tabIndex={-1} className="cp-sect cp-landscape cp-section-anchor" aria-labelledby="cp-landscape-heading">
            <div className="cp-sh">
              <span className="cp-section-icon"><AccountTreeRounded aria-hidden="true"/></span><div className="cp-section-title"><h2 id="cp-landscape-heading">{t("fluss.titel")}</h2>{repoLabel && <span className="repo">{repoLabel}</span>}</div>

              <div className="cp-sh-spacer" />
              {configured && data?.canManage && (
                <Button type="button" className="cp-linkbtn warn" onClick={() => setConfirmUnbind(true)} title={t("fluss.verbindungLoesenTitel")}>
                  <svg viewBox="0 0 24 24"><path d="M18.84 12.25 15 16.09a5 5 0 0 1-7.07 0"/><path d="M5.16 11.75 9 7.91a5 5 0 0 1 7.07 0"/><path d="M2 2l20 20"/></svg>
                  {t("fluss.verbindungLoesen")}
                </Button>
              )}
            </div>
            <div className="cp-flow">
              {flowNodes.map((n, i) => (
                <div key={n.stage} style={{ display: "contents" }}>
                  {i > 0 && <div className={"cp-conn" + (n.active ? " flow" : "")}><ArrowForwardRounded aria-hidden="true"/></div>}
                  <div className={"cp-node " + n.stage + (n.active ? " active" : "") + (n.state === "missing" ? " missing" : (n.state === "pending" ? " pending" : ""))}>
                    <div className="stg"><span className="cp-stage-icon"><LayersOutlined aria-hidden="true"/></span>
                      {/* Beschriftung ueber stageLabel, nicht ueber n.env: n.env ist der Name des
                          GitHub-Environments und wird darunter als Wert weitergereicht. Heute steht
                          in beiden derselbe Text, gemeint sind zwei verschiedene Dinge. */}
                      <span className="stage-label">{stageLabel(n.stage)}</span>
                      {/* Kein eigener Status-Punkt mehr neben der Lasche: die env-Zeile
                          darunter sagt dasselbe ("Noch nicht synchronisiert" bzw. das
                          Environment) - der Punkt war reine Doppelung. */}
                    </div>
                    <div className="inst">{n.instanceName || t("fluss.keineInstanz")}</div>
                    <div className="env">{!n.present ? t("fluss.nichtGetaggt") : n.synced ? t("fluss.environment", { env: n.env }) : t("fluss.nochNichtSynchronisiert")}</div>
                    {n.state === "ready" && !n.active && <div className="cp-stage-ready"><CheckCircleOutlineRounded aria-hidden="true"/>{t("fluss.bereit")}</div>}
                    {n.active && <div className="runbadge"><span className="d" />{t("fluss.transportLaeuft")}</div>}
                  </div>
                </div>
              ))}
            </div>
          </section>


          <section id="cp-repository" tabIndex={-1} className="cp-section-anchor" aria-label={t("navigation.repository")}>
          {!localReads && <CicdWebhookSettings key={`webhook-${spaceId}`} spaceId={spaceId} canManage={!!data?.canManage}/>}
          <CicdAutomations key={`${spaceId}:${localReads}:${readModel?.bindingKey || ""}:${readModel?.generation || ""}`} spaceId={spaceId} autos={autos} updData={updData}
            deferred={localReads} forceExpanded={localReads && repositoryEnabled} openRequest={repositoryOpenRequest}
            onExpand={localReads ? () => setRepositoryScope(readScope) : undefined}
            updLoading={updLoading} updError={updError} updMsg={updMsg} canManage={!!data?.canManage}
            activeCount={active.length} sapUpdating={sapUpdating} extUpdating={extUpdating}
            rueckbauBusy={rueckbauBusy} onCheckUpdates={checkUpdates} onUpdateSap={updateSapBase}
            onUpdateExtension={updateExtension} onSwitchToSap={() => { setRueckbauBlocker([]); setConfirmRueckbau(true); }}>
            {localReads && repositoryEnabled && <>
              <CicdWebhookSettings key={`webhook-${spaceId}`} spaceId={spaceId} canManage={!!data?.canManage}/>
              {configured && data?.canManage && <div className="cp-sect"><BranchRules key={`rules-${spaceId}`} spaceId={spaceId}/></div>}
            </>}
          </CicdAutomations>
          {/* Schreibregeln der Pipeline-Branches (Vertrag 25.3): nur für Verwalter, wenn die Pipeline eingerichtet ist. */}
          {!localReads && configured && data?.canManage && <div className="cp-sect"><BranchRules key={`rules-${spaceId}`} spaceId={spaceId}/></div>}
          </section>

          {configured && (
            <section id="cp-release" tabIndex={-1} className="cp-sect cp-release cp-section-anchor" aria-labelledby="cp-release-heading">
              <div className="cp-sh">
                <span className="cp-section-icon"><RocketLaunchOutlined aria-hidden="true"/></span><h2 id="cp-release-heading">{t("rc.titel")}</h2>
              </div>
              <div className="cp-rc-foot">
                <span className="cp-rc-hint">{t("rc.freigabeHinweis")}</span>
                <Button component="a" className="cp-rc-btn iio-btn--signal" href={`/approvals?spaceId=${encodeURIComponent(spaceId)}`} onClick={event => {
                  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                  event.preventDefault();
                  window.history.pushState({}, "", event.currentTarget.getAttribute("href"));
                  window.dispatchEvent(new PopStateEvent("popstate"));
                }}>
                  {t("rc.freigabeKnopf")}
                </Button>
              </div>
            </section>
          )}

        </>
      )}

      {/* MUI owns focus trapping; the portal paper retains the iio theme scope. */}
      {confirmUnbind && data?.canManage && (
        <MonitorDialog className="cp-dialog iio-modal" aria-labelledby="cp-unbind-t"
          onClose={() => !unbinding && setConfirmUnbind(false)}>
            <div className="iio-modal-head">
              <div className="iio-modal-head-left">
                <span className="iio-modal-eyebrow">{t("loesen.eyebrow", { name: spaces.find(s => s.id === spaceId)?.name || "" })}</span>
                <h2 id="cp-unbind-t">{t("loesen.titel", { repo: repoLabel || t("loesen.repoFallback") })}</h2>
              </div>
              <Button type="button" className="iio-modal-close" onClick={() => !unbinding && setConfirmUnbind(false)} aria-label={t("gemeinsam:aktion.schliessen")} disabled={unbinding}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </Button>
            </div>
            <div className="iio-modal-body">
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: "var(--iio-text-2)" }}>
                {t("loesen.text")}
              </p>
            </div>
            <div className="iio-modal-foot">
              <div className="iio-modal-foot-left" />
              <div className="iio-modal-foot-right">
                <Button type="button" className="iio-btn iio-btn--secondary" onClick={() => setConfirmUnbind(false)} disabled={unbinding}>{t("gemeinsam:aktion.abbrechen")}</Button>
                {/* --warning, nicht --danger: die Aktion ist reversibel und zerstoert nichts.
                    Repository, Environments und Instanzen bleiben, verbinden geht jederzeit
                    wieder - so steht es auch im Dialogtext darueber. Passt zum oeffnenden
                    Knopf .cp-linkbtn.warn. */}
                <Button type="button" className="iio-btn iio-btn--warning" onClick={doUnbind} disabled={unbinding}>
                  {unbinding ? t("loesen.laeuft") : t("loesen.knopf")}
                </Button>
              </div>
            </div>
        </MonitorDialog>
      )}

      {/* Existing mode-switch confirmation, with its original permission gates. */}
      {confirmRueckbau && data?.canManage && (
        <MonitorDialog className="cp-dialog iio-modal" aria-labelledby="cp-rueckbau-t"
          onClose={() => !rueckbauBusy && setConfirmRueckbau(false)}>
            <div className="iio-modal-head">
              <div className="iio-modal-head-left">
                <span className="iio-modal-eyebrow">{t("modus.rueckbauEyebrow")}</span>
                <h2 id="cp-rueckbau-t">{t("modus.rueckbauTitel")}</h2>
              </div>
              <Button type="button" className="iio-modal-close" onClick={() => !rueckbauBusy && setConfirmRueckbau(false)} aria-label={t("gemeinsam:aktion.schliessen")} disabled={rueckbauBusy}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </Button>
            </div>
            <div className="iio-modal-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: "var(--iio-text-2)" }}>
                {t("modus.rueckbauText")}
              </p>

              {updData?.erweiterung?.dateien?.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "var(--iio-text-2)", marginBottom: 6 }}>{t("modus.rueckbauEntfernt")}</div>
                  <ul style={{ margin: 0, padding: "0 0 0 16px", fontSize: 12, color: "var(--iio-text-1)", lineHeight: 1.6 }}>
                    {updData.erweiterung.dateien.map(d => <li key={d} style={{ fontFamily: "var(--iio-font-sans)", fontSize: 12 }}>{d}</li>)}
                  </ul>
                </div>
              )}

              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--iio-text-2)", marginBottom: 6 }}>{t("modus.rueckbauBleibt")}</div>
                <p style={{ margin: 0, fontSize: 12.5, color: "var(--iio-text-2)", lineHeight: 1.5 }}>{t("modus.rueckbauBleibtText")}</p>
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--iio-text-2)", marginBottom: 6 }}>{t("modus.rueckbauVerliert")}</div>
                <ul style={{ margin: 0, padding: "0 0 0 16px", fontSize: 12.5, color: "var(--iio-text-2)", lineHeight: 1.7 }}>
                  <li>{t("modus.rueckbauVerliert1")}</li>
                  <li>{t("modus.rueckbauVerliert2")}</li>
                  <li>{t("modus.rueckbauVerliert3")}</li>
                </ul>
              </div>

              {rueckbauBlocker.length > 0 && (
                <div style={{ borderLeft: "2px solid var(--iio-warning)", paddingLeft: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "var(--iio-text-2)", marginBottom: 6 }}>{t("modus.rueckbauLaufend")}</div>
                  <ul style={{ margin: 0, padding: "0 0 0 16px", fontSize: 12.5, lineHeight: 1.7 }}>
                    {rueckbauBlocker.map(l => (
                      <li key={l.id}>
                        <a href={l.html_url} target="_blank" rel="noreferrer" style={{ color: "var(--iio-link)" }}>{`#${l.id}`}</a>
                        {` \u00b7 ${l.status}`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <p style={{ margin: 0, fontSize: 12.5, color: "var(--iio-text-3)", lineHeight: 1.5 }}>
                {t("modus.rueckbauHinweis")}
              </p>
            </div>
            <div className="iio-modal-foot">
              <div className="iio-modal-foot-left" />
              <div className="iio-modal-foot-right">
                <Button type="button" className="iio-btn iio-btn--secondary" onClick={() => setConfirmRueckbau(false)} disabled={rueckbauBusy}>{t("gemeinsam:aktion.abbrechen")}</Button>
                {/* --warning, not --danger: the action is reversible via "nachladen".
                    No data is destroyed, only the extension files are removed. */}
                <Button type="button" className="iio-btn iio-btn--warning" onClick={doRueckbau} disabled={rueckbauBusy}>
                  {rueckbauBusy ? t("modus.rueckbauLaeuft") : (rueckbauBlocker.length ? t("modus.rueckbauErneut") : t("modus.rueckbauKnopf"))}
                </Button>
              </div>
            </div>
        </MonitorDialog>
      )}

      <ToastContainer toasts={toasts} />
    </div>
  );
}
